import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react'
import { useState } from 'react'
import api from '../services/api'
import { MapContainer, TileLayer, Marker, Popup, AttributionControl } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
})
L.Marker.prototype.options.icon = DefaultIcon

const contactSchema = z.object({
  nom: z.string().min(2, "Le nom ou nom de l'entreprise est requis"),
  email: z.string().email("Email invalide"),
  thematique: z.string().min(2, "La thématique est requise"),
  duree: z.enum(["3 jours", "5 jours"], {
    errorMap: () => ({ message: "Veuillez choisir une durée" })
  }),
  periode: z.string().min(2, "La période souhaitée est requise"),
  lieu: z.enum(["En ligne", "Présentiel", "Hybride"], {
    errorMap: () => ({ message: "Veuillez choisir un lieu" })
  }),
  objectif: z.string().min(10, "L'objectif doit faire au moins 10 caractères"),
})

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [feedback, setFeedback] = useState(null)

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(contactSchema)
  })

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    setFeedback(null)
    
    // Format training details into the message field for the backend
    const formattedMessage = `
--- Demande de Formation ---
Thématique: ${data.thematique}
Durée: ${data.duree}
Période: ${data.periode}
Lieu: ${data.lieu}
Objectif recherché: 
${data.objectif}
-----------------------------
`
    const finalData = {
      nom: data.nom,
      email: data.email,
      objet: "Demande de Formation (via site web)",
      message: formattedMessage
    }

    try {
      await api.post('/contact/', finalData)
      setFeedback({ type: 'success', message: 'Votre message a été envoyé avec succès !' })
      reset()
    } catch (error) {
      setFeedback({ type: 'error', message: 'Une erreur est survenue. Veuillez réessayer plus tard.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="pt-24 pb-16">
      <div 
        className="text-white py-20 mb-12 relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: 'url("/images/contact-bg.jpeg")' }}
      >
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-cidh-gold/80"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="flex flex-col items-center text-center">
            <div className="bg-white p-3 rounded-2xl mb-6 shadow-lg">
              <Mail size={40} className="text-cidh-gold" />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold font-montserrat mb-6 tracking-tight">Contactez-nous</h1>
            <p className="max-w-2xl mx-auto text-lg leading-relaxed text-white">
              Une question ? Un projet ? Notre équipe est à votre écoute pour vous accompagner.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 text-inherit animate-fade-in">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
            <h2 className="text-2xl font-bold text-cidh-blue mb-8 font-montserrat">Envoyez un message</h2>
            
            {feedback && (
              <div className={`p-4 rounded-md mb-6 ${feedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {feedback.message}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Nom (Personne ou entreprise) *</label>
                  <input {...register('nom')} className="w-full p-3 border rounded-md outline-cidh-blue" placeholder="Votre nom ou structure" />
                  {errors.nom && <span className="text-red-500 text-xs">{errors.nom.message}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Email *</label>
                  <input {...register('email')} className="w-full p-3 border rounded-md outline-cidh-blue" placeholder="votre@email.com" />
                  {errors.email && <span className="text-red-500 text-xs">{errors.email.message}</span>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Thème de la formation souhaitée *</label>
                <input {...register('thematique')} className="w-full p-3 border rounded-md outline-cidh-blue" placeholder="Ex: Gestion de projet, RH, Intelligence Artificielle..." />
                {errors.thematique && <span className="text-red-500 text-xs">{errors.thematique.message}</span>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Durée en Jour *</label>
                  <select {...register('duree')} className="w-full p-3 border rounded-md outline-cidh-blue bg-white">
                    <option value="">Sélectionnez la durée</option>
                    <option value="3 jours">3 jours</option>
                    <option value="5 jours">5 jours</option>
                  </select>
                  {errors.duree && <span className="text-red-500 text-xs">{errors.duree.message}</span>}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Période souhaitée *</label>
                  <input {...register('periode')} className="w-full p-3 border rounded-md outline-cidh-blue" placeholder="Ex: Mai 2024, Dès que possible..." />
                  {errors.periode && <span className="text-red-500 text-xs">{errors.periode.message}</span>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Lieu *</label>
                <select {...register('lieu')} className="w-full p-3 border rounded-md outline-cidh-blue bg-white">
                  <option value="">Sélectionnez le lieu</option>
                  <option value="En ligne">En ligne</option>
                  <option value="Présentiel">Présentiel</option>
                  <option value="Hybride">Hybride</option>
                </select>
                {errors.lieu && <span className="text-red-500 text-xs">{errors.lieu.message}</span>}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Objectif recherché *</label>
                <textarea {...register('objectif')} rows="4" className="w-full p-3 border rounded-md outline-cidh-blue" placeholder="Décrivez vos attentes et objectifs pour cette formation..."></textarea>
                {errors.objectif && <span className="text-red-500 text-xs">{errors.objectif.message}</span>}
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isSubmitting ? <Loader2 className="animate-spin" /> : <Send size={20} />}
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
              </button>
            </form>
          </div>

          {/* Info */}
          <div className="space-y-12">
            <div>
              <h2 className="text-2xl font-bold text-cidh-blue mb-6 font-montserrat">Coordonnées</h2>
              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <div className="bg-cidh-gold/20 p-3 rounded-full text-cidh-gold">
                    <MapPin />
                  </div>
                  <div>
                    <h4 className="font-bold">Siège Social</h4>
                    <p className="text-gray-600">Abidjan, II-Plateaux, Las Palmas, Bld des Martyrs – Immeuble SICOGI B, Tour i, 1er étage, porte 101 et 102</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="bg-cidh-gold/20 p-3 rounded-full text-cidh-gold">
                    <Phone />
                  </div>
                  <div>
                    <h4 className="font-bold">Téléphone</h4>
                    <p className="text-gray-600">Tél. : (+225) 27 22 27 49 09</p>
                    <p className="text-gray-600">Cél : (+225) 07 07 58 31 46 / 07 07 48 52 33 / 01 71 59 27 81</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="bg-cidh-gold/20 p-3 rounded-full text-cidh-gold">
                    <Mail />
                  </div>
                  <div>
                    <h4 className="font-bold">Email</h4>
                    <p className="text-gray-600">cidhconsulting@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-cidh-blue mb-6 font-montserrat">Bureaux Régionaux</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-cidh-gray-light p-4 rounded-lg">
                  <h4 className="font-bold text-cidh-green">Bureau de Man</h4>
                  <p className="text-sm text-gray-600">Face à la préfecture</p>
                </div>
                <div className="bg-cidh-gray-light p-4 rounded-lg">
                  <h4 className="font-bold text-cidh-green">Bureau de Boundiali</h4>
                  <p className="text-sm text-gray-600">Quartier résidentiel</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="h-64 rounded-xl overflow-hidden shadow-sm border border-gray-100 relative z-0">
              <a href="https://www.google.com/maps/search/?api=1&query=Abidjan+II-Plateaux+Las+Palmas+Bld+des+Martyrs+Immeuble+SICOGI+B+Tour+i" target="_blank" rel="noopener noreferrer" className="absolute right-2 top-2 z-[400] bg-white px-3 py-1 text-sm font-semibold text-cidh-blue rounded-md shadow-md hover:bg-gray-50 flex items-center gap-1 transition-colors">
                <MapPin size={16} />
                Voir sur Google Maps
              </a>
              <MapContainer 
                center={[5.378, -3.993]} 
                zoom={14} 
                scrollWheelZoom={false} 
                style={{ height: '100%', width: '100%' }}
                attributionControl={false}
              >
                <AttributionControl prefix="Leaflet" />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[5.378, -3.993]}>
                  <Popup>
                    <strong>CIDH Consulting</strong><br/>
                    Abidjan, II-Plateaux, Las Palmas,<br/>
                    Bld des Martyrs – Immeuble SICOGI B, Tour i,<br/>
                    1er étage, porte 101 et 102
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact
